import { Component, Input, inject, EventEmitter, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ModalFormContainerComponent } from '../../ui/modal-form-container/modal-form-container.component';
import { InputFieldComponent } from '../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../ui/customizable-button/customizable-button.component';
import { countries } from '../../../../utils/countriesList';
import { IOrganization } from '../../../types/contactModule';
import { industries } from '../../../../utils/staticData';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { humanizeEnum } from '../../tables/group-table/group-rows';

@Component({
  selector: 'edit-organization-form',
  standalone: true,
  imports: [ModalFormContainerComponent, InputFieldComponent, CustomizableButtonComponent, ReactiveFormsModule],
  templateUrl: './edit-organization-form.component.html',
  styleUrls: ['../../tables/group-table/group-form.css', './edit-organization-form.component.css'],
})
export class EditOrganizationFormComponent implements OnChanges {
  @Output() closeModal = new EventEmitter();
  @Input({ required: true }) show: boolean = false;
  @Input({ required: true }) organization: IOrganization | null = null;

  // static data
  countries = countries;
  industries = industries.map((industry) => ({ value: industry.value, label: humanizeEnum(industry.value) }));

  // reactive data
  submitted: boolean = false;
  showModal: boolean = false;
  saveError = '';

  // services
  contactsService = inject(ContactsService);
  builder = inject(FormBuilder);

  organizationForm = this.builder.group({
    name: ['', Validators.required],
    description: [''],
    country: ['', Validators.required],
    city: ['', Validators.required],
    region: ['', Validators.required],
    industry: ['', Validators.required],
  });

  getFormInput(name: keyof IOrganization) {
    return this.organizationForm.get(name);
  }

  close() {
    this.closeModal.emit();
  }

  get contactServiceLoading() {
    return this.contactsService.isLoading;
  }

  ngOnChanges(changes: SimpleChanges) {
    const opening = !!changes['show'] && this.show && !changes['show'].previousValue;
    if (changes['organization'] || opening) {
      // Fill every field, so blanks in this record do not keep the last one's values.
      const org = this.organization;
      this.organizationForm.reset({
        name: org?.name ?? '',
        description: org?.description ?? '',
        country: org?.country ?? '',
        city: org?.city ?? '',
        region: org?.region ?? '',
        industry: org?.industry ?? '',
      });
      this.submitted = false;
      this.saveError = '';
    }
    this.showModal = this.show;
  }

  handleSubmit() {
    this.submitted = true;
    this.saveError = '';
    if (this.organizationForm.valid) {
      this.contactsService
        .updateOrganization(
          { ...this.organization, ...(this.organizationForm.value as IOrganization) },
          this.organization?.uuid as string
        )
        .subscribe({
          next: () => {
            this.close();
          },
          error: () => {
            this.saveError = 'Your changes were not saved. Check your connection and try again.';
          },
        });
    }
  }
}
